/**
 * Theme sync: 2026-09-03-base
 * jrs-v2-flow-bundle.js — Bundle PDP flow orchestrator (Phase 2)
 *
 * Doc 6 §11.2 flow orchestrator role. Doc 2 §6 bundle parent payload.
 *
 * The bundle PDP wraps multiple child products in a single configuration.
 * Phase 2 lands the orchestrator with a simplified-but-honest sequence:
 *
 *   intro → position_picker → application_method
 *         → [logo_strategy if positions.length >= 2 else skip]
 *         → logo_picker (loops per position if 'different_logos')
 *         → review
 *
 * The full Doc 6 §11.2 bundle shape ("for each child product, run mini-
 * flow ...") is left for Phase 3 polish — it requires per-child state
 * tracking (state.draft.bundle_children = { [childProductId]: { ... }})
 * which adds significant orchestration complexity beyond the scope of
 * the Phase 2 "lands and is QA-testable in unpublished theme" target.
 *
 * For Phase 2 the bundle flow uses the same step controllers as
 * multistep, with these differences honoured at the controller level:
 *   - state.flow === 'bundle' is read by review and (later) by the
 *     cart-submit adapter so Doc 2 §6 bundle parent payload shape applies
 *   - context.bundleProductVariantId is required per Doc 2 §13.13 (v0.4)
 *
 * Bundle-specific copy and bundle-wide vs per-product strategy nuances
 * are Phase 3 work. This orchestrator is the entry point + nextStep
 * decision module for the Phase 2 simplified shape.
 *
 * Public API:
 *   JRS.FlowBundle.start(context)
 *     - context: { productId, productHandle, productCategoryHandle,
 *                  productTags, productPricing, bundleProductVariantId,
 *                  bundleProductHandle, bundleMetaobjectId, categories }
 *     - bundleProductVariantId is REQUIRED per Doc 2 §13.13.
 */

window.JRS = window.JRS || {};

(function (JRS) {
  'use strict';

  var FLOW_ID = 'bundle';

  var TOTAL_STEPS_DEFAULT = 5; // intro + position_picker + application_method + logo_picker + review

  /**
   * @param {Object} context - PDP context to pass to the shell
   */
  function start(context) {
    if (!JRS.CustomizerPopup || typeof JRS.CustomizerPopup.open !== 'function') {
      console.error('JRS.FlowBundle.start: JRS.CustomizerPopup is not available');
      return;
    }

    var ctx = context || {};

    // Doc 2 §13.13 (v0.4): bundleProductVariantId is the cart-payload
    // contract input. Warn loudly if the trigger didn't supply it; the
    // cart-submit adapter (follow-up session) will surface this as a
    // hard error.
    if (typeof ctx.bundleProductVariantId !== 'number' || ctx.bundleProductVariantId <= 0) {
      if (window.console && console.warn) {
        console.warn(
          'JRS.FlowBundle.start: context.bundleProductVariantId is missing or invalid. ' +
          'Cart-payload-builder Doc 2 §13.13 requires this field. Trigger code should ' +
          'pass product.selected_or_first_available_variant.id from the bundle PDP Liquid.'
        );
      }
    }

    var entry = resolveEntry();
    JRS.CustomizerPopup.open({
      flow: FLOW_ID,
      source: 'pdp',
      trigger: 'flow_start',
      // Phase 2.5 — bundle flow's step 1 is branding_strategy, NOT intro.
      // The branding_strategy controller asks "Same logo across all items
      // or different per product?"; intro (multistep) asks "Add branding
      // or skip?". Single-selection bundles skip branding_strategy — see
      // resolveEntry().
      entryStep: entry.entryStep,
      entryStepIndex: entry.entryStepIndex,
      totalSteps: entry.totalSteps,
      draft: entry.draft,
      context: ctx
    });
  }

  /**
   * Resolve the bundle flow's entry. A single-SELECTION bundle — exactly one
   * active category (the customer configured one product) — skips the
   * branding_strategy screen, because "same logo across all vs customise each
   * product" is a meaningless choice with one product. It enters directly at
   * position_picker in bundle_wide mode, with _branding_strategy_skipped so the
   * step counter shifts down by one. Two-or-more categories keep step 1.
   *
   * @returns {{entryStep:string, entryStepIndex:number, totalSteps:number, draft:(Object|null)}}
   */
  function resolveEntry() {
    var State = JRS.CustomizerPopupState;
    var single = false;
    try {
      single = !!(State && typeof State.getActiveCategoryList === 'function' &&
                  State.getActiveCategoryList().length === 1);
    } catch (e) {}
    if (single) {
      return {
        entryStep: 'position_picker',
        entryStepIndex: 1,
        totalSteps: TOTAL_STEPS_DEFAULT - 1,
        draft: { branding_strategy: 'bundle_wide', _branding_strategy_skipped: true }
      };
    }
    // Headwear in a MULTI-category bundle → per-product is mandatory: headwear's
    // logo positions differ from garments, so "same logo across all" can't
    // represent it. Enter at branding_strategy, which renders an intro (not the
    // same-vs-different cards) and forces per_product. (A single headwear
    // category is handled by the `single` branch above — bundle_wide with that
    // category's own positions is fine when there's nothing to differ from.)
    if (isHeadwearBundle()) {
      return {
        entryStep: 'branding_strategy',
        entryStepIndex: 1,
        totalSteps: TOTAL_STEPS_DEFAULT,
        draft: { branding_strategy: 'per_product', _force_per_product: true }
      };
    }
    return {
      entryStep: 'branding_strategy',
      entryStepIndex: 1,
      totalSteps: TOTAL_STEPS_DEFAULT,
      draft: null
    };
  }

  // A category whose name reads as "headwear" (legacy convention: a bundle
  // category titled Headwear). Strips non-letters so "Head Wear" / "Custom
  // Headwear" also match.
  function _isHeadwearName(name) {
    if (!name) return false;
    return String(name).toLowerCase().replace(/[^a-z]/g, '').indexOf('headwear') !== -1;
  }

  // True when the bundle's active categories include a headwear category.
  function isHeadwearBundle() {
    var State = JRS.CustomizerPopupState;
    var list = (State && typeof State.getActiveCategoryList === 'function')
      ? State.getActiveCategoryList() : [];
    for (var i = 0; i < list.length; i++) {
      if (list[i] && _isHeadwearName(list[i].name)) return true;
    }
    return false;
  }

  /**
   * Compute the next step. Bundle flow:
   *   branding_strategy → position_picker → application_method
   *     → [logo_strategy if ≥2 positions, else skip]
   *     → logo_picker (loops per position if logo_strategy='different')
   *     → review
   *
   * The per-child sequencing nuance for bundle_wide vs per_product is
   * deferred to a later phase; Phase 2.5 keeps the sequence identical
   * to multistep after step 1.
   */
  function nextStep(currentStep, state) {
    var draft = (state && state.draft) || {};
    var positions = draft.positions || [];

    switch (currentStep) {
      case 'branding_strategy':
        // Both choices advance to position_picker for now. Per-product
        // sequencing (looping through children) is Phase 3 polish.
        return 'position_picker';

      case 'intro':
        // Defensive — bundle flow doesn't enter intro, but if a state
        // restoration somehow lands here treat 'yes' as advancing.
        return draft.intro_choice === 'yes' ? 'position_picker' : null;

      case 'position_picker':
        return 'application_method';

      case 'application_method':
        if (positions.length >= 2) return 'logo_strategy';
        _prepareLogoPickerCursor(draft, 0);
        return 'logo_picker';

      case 'logo_strategy':
        _prepareLogoPickerCursor(draft, 0);
        return 'logo_picker';

      case 'logo_picker': {
        if (draft.logo_strategy === 'different_logos') {
          var nextCursor = (typeof draft._position_cursor === 'number' ? draft._position_cursor : 0) + 1;
          if (nextCursor < positions.length) {
            _prepareLogoPickerCursor(draft, nextCursor);
            return 'logo_picker';
          }
        }
        return 'review';
      }

      case 'logo_text_builder':
        return 'logo_picker';

      case 'review':
        return null;

      default:
        return null;
    }
  }

  function _prepareLogoPickerCursor(draft, cursor) {
    var positions = draft.positions || [];
    if (positions.length === 0) return;
    var safeCursor = Math.max(0, Math.min(cursor, positions.length - 1));
    JRS.CustomizerPopupState.dispatch({
      type: 'SET_DRAFT',
      payload: {
        draft: _merge(draft, {
          _position_cursor: safeCursor,
          _current_position_for_logo: positions[safeCursor]
        })
      }
    });
  }

  function _merge(a, b) {
    var out = {};
    for (var k1 in a) {
      if (Object.prototype.hasOwnProperty.call(a, k1)) out[k1] = a[k1];
    }
    for (var k2 in b) {
      if (Object.prototype.hasOwnProperty.call(b, k2)) out[k2] = b[k2];
    }
    return out;
  }

  // -------------------------------------------------------------------
  // Per-product outer loop (Phase per_product S1 — spec D3/D4 + Decision 2/5)
  // -------------------------------------------------------------------
  // NOTE: routing in this flow is driven by the step CONTROLLERS calling
  // JRS.CustomizerPopup.goToStep(...) with explicit step names. nextStep()
  // above is consumed only by the shell's step counter (computeTotalSteps /
  // stepIndexFor), NOT for routing. So the per_product category loop is
  // exposed as a helper the logo_picker controller calls at its
  // category-complete exit point (where it would otherwise route to review).

  /**
   * Called when a per_product category's sub-flow (position_picker → method →
   * [logo_strategy] → logo_picker per-position loop) has finished. Snapshots
   * the just-finished category's flat config under its REAL category index,
   * advances the dense cursor, and returns the next step: 'position_picker'
   * for the next active category (after resetting the flat scratch), or
   * 'review' when every active category is done. For non-per_product
   * (bundle_wide / multistep) returns 'review' unchanged — no snapshot, no
   * cursor change.
   *
   * @param {Object} state - current popup state (live state is re-read internally)
   * @returns {string} next step id
   */
  function completeCategoryOrAdvance(state) {
    var State = JRS.CustomizerPopupState;
    var draft = (State && typeof State.getState === 'function' && State.getState().draft) ||
                (state && state.draft) || {};

    if (draft.branding_strategy !== 'per_product') return 'review';

    // Edit-from-review (Phase per_product S3, spec D9). When the customer
    // edited ONE category from the review screen, _edit_category_index holds
    // its REAL index. Snapshot the (just-edited) flat draft back into that
    // index, clear the edit flag, and return to review WITHOUT touching the
    // forward cursor. This is the only addition to completeCategoryOrAdvance's
    // semantics permitted by the S3 brief; the forward-loop path below is
    // unchanged (forward flow never sets _edit_category_index).
    if (typeof draft._edit_category_index === 'number' && draft._edit_category_index >= 0) {
      var editIdx = draft._edit_category_index;
      if (State && typeof State.snapshotCategoryConfig === 'function') {
        State.snapshotCategoryConfig(editIdx);
      }
      _clearEditCategoryIndex();
      return 'review';
    }

    var activeList = (State && typeof State.getActiveCategoryList === 'function')
      ? State.getActiveCategoryList() : [];
    if (!activeList.length) return 'review'; // defensive — nothing to loop

    var cursor = (typeof draft._current_category_index === 'number' && draft._current_category_index >= 0)
      ? draft._current_category_index : 0;

    // Snapshot the just-finished category under its REAL index. The dense
    // cursor maps to activeList[cursor].categoryIndex — sparse-safe when a
    // middle category was filtered out (Decision 2: keys become e.g. 0,2).
    var current = activeList[cursor];
    if (current && State && typeof State.snapshotCategoryConfig === 'function') {
      State.snapshotCategoryConfig(current.categoryIndex);
    }

    var nextCursor = cursor + 1;
    _setCategoryCursor(nextCursor);

    if (nextCursor >= activeList.length) {
      return 'review'; // all active categories complete (cursor == length, spec D4)
    }
    if (State && typeof State.resetFlatDraftForNextCategory === 'function') {
      State.resetFlatDraftForNextCategory();
    }
    return 'position_picker';
  }

  function _setCategoryCursor(n) {
    if (!JRS.CustomizerPopupState || typeof JRS.CustomizerPopupState.dispatch !== 'function') return;
    JRS.CustomizerPopupState.dispatch({
      type: 'SET_DRAFT_FIELD',
      payload: { field: '_current_category_index', value: n }
    });
  }

  // Phase per_product S3 — clear the edit-from-review marker after a category
  // edit has been re-snapshotted, so a subsequent forward action resumes the
  // normal cursor-advance path.
  function _clearEditCategoryIndex() {
    if (!JRS.CustomizerPopupState || typeof JRS.CustomizerPopupState.dispatch !== 'function') return;
    JRS.CustomizerPopupState.dispatch({
      type: 'SET_DRAFT_FIELD',
      payload: { field: '_edit_category_index', value: null }
    });
  }

  // -------------------------------------------------------------------
  // 2026-05-20 Bug 5 fix — dynamic step counter parity with multistep.
  // Bundle V3 is gated off at the trigger (see jrs-v2-pdp-trigger.liquid)
  // pending Doc 2 §6 multi-sub-product revision, but the helpers below
  // keep the shell's auto-correct working for the simplified Phase 2
  // bundle shape (intro + branding_strategy is single-step here; full
  // per-child Phase 3 work doesn't change the counter math).
  // -------------------------------------------------------------------

  // Phase per_product — STABLE (locked) step counter for the per_product loop.
  // The exact total can't be known up front (later categories' position counts +
  // same/different-logo choices are unknown), so the chosen behaviour is a stable
  // estimate: budget a fixed number of steps per category so "Step X of Y" is set
  // once (depends only on the category count) and never jumps as the user advances.
  //
  // Budget = 4 = the most distinct step types one category can show
  // (position_picker + application_method + logo_strategy + logo_picker). 3 would
  // overflow (X > Y) for any category with >=2 positions — those need the
  // logo_strategy step. The per-position logo_picker loop (different_logos) is
  // counted as the single logo step, so it never overflows the budget either.
  // (Trade-off, accepted with the "stable" choice: a 1-position category really
  // uses 3 of its 4 budgeted slots, so the counter shows a small gap at that
  // category boundary. Change the constant to retune the estimate.)
  var _PP_STEPS_PER_CATEGORY = 4;

  function _perProductCategoryCount() {
    var State = JRS.CustomizerPopupState;
    var list = (State && typeof State.getActiveCategoryList === 'function')
      ? State.getActiveCategoryList() : [];
    return (list && list.length) || 0;
  }

  // 1 (branding_strategy) + N categories x budget + 1 (review).
  function _perProductTotalSteps(n) {
    return 1 + (n * _PP_STEPS_PER_CATEGORY) + 1;
  }

  // Stable global step index: 1 (branding) + completed categories x budget +
  // the step's slot within the current category, clamped to [1, total].
  function _perProductStepIndex(stepId, draft, n) {
    var total = _perProductTotalSteps(n);
    if (stepId === 'branding_strategy' || stepId === 'intro') return 1;
    if (stepId === 'review') return total;
    var cursor = (typeof draft._current_category_index === 'number' && draft._current_category_index >= 0)
      ? draft._current_category_index : 0;
    if (cursor > n - 1) cursor = n - 1; // review / edit-from-review land outside the forward range
    var hasStrategy = (draft.positions || []).length >= 2;
    var within;
    switch (stepId) {
      case 'position_picker':    within = 1; break;
      case 'application_method': within = 2; break;
      case 'logo_strategy':      within = 3; break;
      case 'logo_picker':
      case 'logo_text_builder':  within = hasStrategy ? 4 : 3; break;
      default:                   within = 1; break;
    }
    var idx = 1 + (cursor * _PP_STEPS_PER_CATEGORY) + within;
    if (idx < 1) idx = 1;
    if (idx > total) idx = total;
    return idx;
  }

  /**
   * Same shape as the multistep flow because the Phase 2 bundle reuses
   * the same step controllers. The bundle adds branding_strategy as
   * step 1 (replacing intro) when state.flow === 'bundle'; for Phase 2
   * we treat branding_strategy + intro as occupying step 1.
   */
  function computeTotalSteps(state) {
    var draft = (state && state.draft) || {};

    // Phase per_product — stable locked total across the category loop.
    if (draft.branding_strategy === 'per_product') {
      var ppN = _perProductCategoryCount();
      if (ppN > 0) return _perProductTotalSteps(ppN);
      // active category list not ready → fall through to the single-category estimate
    }

    // Single-selection bundles skip branding_strategy → one fewer step.
    var skip = draft._branding_strategy_skipped ? 1 : 0;

    var positions = draft.positions || [];
    var posCount = positions.length;
    var strategy = draft.logo_strategy;

    // Initial default: minimum flow length so the progress bar doesn't
    // read "1 of 4" at branding_strategy entry.
    if (posCount === 0) return 5 - skip;

    var total = 4;
    if (posCount >= 2) total += 1;

    var pickerCount;
    if (posCount === 1) {
      pickerCount = 1;
    } else if (strategy === 'different_logos') {
      pickerCount = posCount;
    } else {
      pickerCount = 1;
    }
    total += pickerCount;

    return total - skip;
  }

  function stepIndexFor(stepId, state) {
    var draft = (state && state.draft) || {};

    // Phase per_product — stable locked step index across the category loop.
    if (draft.branding_strategy === 'per_product') {
      var ppN = _perProductCategoryCount();
      if (ppN > 0) return _perProductStepIndex(stepId, draft, ppN);
    }

    // Single-selection bundles skip branding_strategy → every post-step-1 index
    // shifts down by one.
    var skip = draft._branding_strategy_skipped ? 1 : 0;

    var positions = draft.positions || [];
    var posCount = positions.length;
    var strategy = draft.logo_strategy;
    var hasStrategy = posCount >= 2;

    switch (stepId) {
      case 'branding_strategy':  return 1;
      case 'intro':              return 1; // shared slot with branding_strategy
      case 'position_picker':    return 2 - skip;
      case 'application_method': return 3 - skip;
      case 'logo_strategy':      return hasStrategy ? (4 - skip) : 0;
      case 'logo_picker': {
        var base = (hasStrategy ? 5 : 4) - skip;
        var cursor = typeof draft._position_cursor === 'number' ? draft._position_cursor : 0;
        if (strategy === 'different_logos' && cursor > 0 && cursor < posCount) {
          return base + cursor;
        }
        return base;
      }
      case 'logo_text_builder': {
        var base2 = (hasStrategy ? 5 : 4) - skip;
        var cursor2 = typeof draft._position_cursor === 'number' ? draft._position_cursor : 0;
        if (strategy === 'different_logos' && cursor2 > 0 && cursor2 < posCount) {
          return base2 + cursor2;
        }
        return base2;
      }
      case 'review':             return computeTotalSteps(state);
      default:                   return 0;
    }
  }

  // -------------------------------------------------------------------
  // Public API
  // -------------------------------------------------------------------

  JRS.FlowBundle = {
    FLOW_ID: FLOW_ID,
    start: start,
    resolveEntry: resolveEntry, // single-selection skip / headwear force-per-product entry
    isHeadwearBundle: isHeadwearBundle,
    nextStep: nextStep,
    completeCategoryOrAdvance: completeCategoryOrAdvance, // Phase per_product S1
    computeTotalSteps: computeTotalSteps,
    stepIndexFor: stepIndexFor
  };
})(window.JRS);
